import * as _ from 'lodash';
import { IhmLighthouseServiceClient } from '@services/coralservices/serviceClients/IhmLighthouseServiceClient';
import { TaskCategory } from '@ch-types/TaskCategory';
import { WeblabService } from '@services/WeblabService';

const lodChecklistProperties = {
    POLL_DURATION: 5 * 60 * 1000, // poll for tasks every 5 minutes
    TASK_TIME_RANGE: 12 * 3600 * 1000, // get tasks within 12 hours of current time
};
const FREQUENTLY_USED_TOOL = 'AT_BASE_FREQUENTLY_USED_TOOLS_847158';

let interval;
let currentStoreId;
let currentZone;
let statusPageTasks = [];
let currentZoneTasks = [];
let lastUpdated;

export class LODChecklistService {
    public static updateTasks = () => {
        LODChecklistService.stopInterval();
        const promise = IhmLighthouseServiceClient.getActiveTasksByCategory(currentStoreId, TaskCategory.LOD_CHECKLIST);
        return promise
            .then((response) => {
                const currentTasks = _.filter(response.tasks, (task) => {
                    return LODChecklistService.isRecent(task) && LODChecklistService.isIncomplete(task);
                });
                statusPageTasks = _.filter(currentTasks, LODChecklistService.shouldShowInStatusPage);
                currentZoneTasks = _.filter(currentTasks, LODChecklistService.isInCurrentZone);
                lastUpdated = new Date().getTime();
            })
            .catch((error) => {
                console.error('failed to fetch LOD tasks ', error);
                return Promise.reject(error);
            })
            .finally(() => {
                interval = setTimeout(LODChecklistService.updateTasks, lodChecklistProperties.POLL_DURATION);
            });
    };

    public static initialize = (storeId) => {
        currentStoreId = storeId;
        return LODChecklistService.updateTasks();
    };

    public static changeZone = (zone) => {
        currentZone = zone;
        LODChecklistService.updateTasks();
    };

    public static getStatusPageTasks = () => {
        return {
            lastUpdated,
            tasks: statusPageTasks,
        };
    };

    public static getCurrentZoneTasks = () => {
        return {
            lastUpdated,
            tasks: currentZoneTasks,
        };
    };

    public static stopInterval = () => {
        if (interval) {
            clearTimeout(interval);
            interval = null;
        }
    };

    private static isInCurrentZone = (task) => {
        if (WeblabService.isFeatureEnabledWithCache(FREQUENTLY_USED_TOOL)) {
            return true;
        }
        return task.taskParams.assignedZone === currentZone;
    };

    private static hasSla = (task) => {
        return !!task.taskParams.SLA && parseInt(task.taskParams.SLA, 10) > 0;
    };

    private static isUrgent = (task) => {
        return task.taskParams.priority === 'HIGH';
    };

    private static isIncomplete = (task) => {
        return task.taskParams.status === 'INCOMPLETE';
    };

    private static isPastSla = (task) => {
        return LODChecklistService.hasSla(task) && new Date() > new Date(parseInt(task.taskParams.occurrenceTimestamp, 10) + parseInt(task.taskParams.SLA, 10) * 60000);
    };

    private static isRecent = (task) => {
        // task has occurred and is less than 12 hours old
        const halfDay = lodChecklistProperties.TASK_TIME_RANGE;
        const now = new Date().getTime();
        const occurrenceTimestamp = parseInt(task.taskParams.occurrenceTimestamp, 10);

        return occurrenceTimestamp && now >= occurrenceTimestamp && now - halfDay < occurrenceTimestamp;
    };

    private static shouldShowInStatusPage = (task) => {
        /* should show tasks matching the following rules:
            1. urgent tasks in ANY zone which have occurred AND passed SLA
            2. urgent tasks in my zone which have occurred
            3. non-urgent tasks in my zone with no SLA which have occurred
            4. non-urgent tasks in my zone with SLA which have occurred AND not passed SLA (reminder tasks)
        */

        return (
            (LODChecklistService.isUrgent(task) && LODChecklistService.isPastSla(task)) ||
            (LODChecklistService.isInCurrentZone(task) && (LODChecklistService.isUrgent(task) || (!LODChecklistService.isUrgent(task) && !LODChecklistService.isPastSla(task))))
        );
    };
}
